import { IPcr } from '../../pcr/store/pcr.types'
import { getSha256 } from '../../../utilities'
import { isOnline } from '../../../utilities/isOnline.util'
import { PcrBackupMetadataService } from './pcrBackupMetadata.service'
import { PcrBackupApiService } from './pcrBackupApi.service'
import { IPcrBackupConfig, IPcrBackupMetadata } from '../store/pcrBackup.types'
import { SubmitChartService } from '../../submitChart/services/submitChart.service'
import { IFetchPRIDResponse } from '../../submitChart/store/submitChart.types'
import { setBackupMetadata, persistPrid } from '../store/pcrBackup.actions'
import { getPcrBackupConfigSelector } from '../store/pcrBackup.selectors'
import Logger from '../../../services/logger.service'
import { store } from '../../../index'

export interface BackupResult {
  success: boolean
}

/**
 * Module-level in-flight guard. Prevents concurrent backup calls for the same
 * PCR from both reaching the server — avoids unique-constraint violations when
 * multiple triggers fire back-to-back
 */
const backupInFlight = new Set<string>()

/**
 * Base delay (ms) for exponential-backoff retries.
 * Sequence: 3 s, 6 s, 12 s, …
 */
const RETRY_BASE_DELAY_MS = 3000

/**
 * Keys excluded from the checksum so transient/UI-only mutations
 * do not force spurious backups.
 */
/**
 * Excluded from checksum — transient/UI-only fields and _attachments (large base64
 * signature images) that should not drive spurious backups.
 */
const CHECKSUM_EXCLUDED_KEYS: Array<keyof IPcr> = ['rules', '_uiPlaceHolder', '_attachments']

/**
 * Returns a stable JSON string from the PCR payload:
 * 1. Excluded fields removed (transient UI/rules data)
 * 2. Keys sorted recursively for deterministic SHA-256
 */
function buildChecksumInput(pcr: IPcr): string {
  const clone: Partial<IPcr> = { ...pcr }
  for (const key of CHECKSUM_EXCLUDED_KEYS) {
    delete clone[key]
  }
  return JSON.stringify(sortKeysDeep(clone))
}

/**
 * Builds the payload sent to the backup API.
 * Only strips rules (server-owned, not user data).
 */
function buildServerPayload(pcr: IPcr): string {
  const { rules, ...rest } = pcr
  return JSON.stringify(rest)
}

function sortKeysDeep(obj: any): any {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    return Array.isArray(obj) ? obj.map(sortKeysDeep) : obj
  }
  return Object.keys(obj)
    .sort()
    .reduce((sorted: any, key) => {
      sorted[key] = sortKeysDeep(obj[key])
      return sorted
    }, {})
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Attempts to back up the PCR with exponential-backoff retry.
 * delay = RETRY_BASE_DELAY_MS * 2^attempt  (3 s, 6 s, 12 s, …)
 */
async function sendBackupWithRetry(
  api: PcrBackupApiService,
  pcrId: string,
  pcrContent: string,
  config: IPcrBackupConfig
): Promise<boolean> {
  for (let attempt = 0; attempt < config.retryCount; attempt++) {
    try {
      const response = await api.backupPcr({
        pcrContent,
        options: { forceSnapshot: false, debugLogging: true },
      })
      if (response?.status === 200) {
        if (response.data) {
          Logger.info(
            `PcrBackupService: Backup succeeded — ${response.data.message} | s3Key=${response.data.s3Key} | version=${response.data.latestVersion} | isSnapshot=${response.data.isSnapshot} | pcrId=${pcrId}`
          )
        }
        return true
      }
      const errorMessages = response?.messages?.map((m) => `[${m.level?.name ?? 'Unknown'}] ${m.message}`).join('; ')
      Logger.warn(
        `PcrBackupService: Backup attempt ${attempt + 1} failed — status=${response?.status}${
          errorMessages ? ` | ${errorMessages}` : ''
        }. pcrId=${pcrId}`
      )
    } catch (err) {
      Logger.errorObj(`PcrBackupService: Backup attempt ${attempt + 1} threw for pcrId=${pcrId}`, err)
    }
    if (attempt < config.retryCount - 1) {
      const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt)
      Logger.info(`PcrBackupService: Retrying in ${delay}ms (attempt ${attempt + 2} of ${config.retryCount})`)
      await sleep(delay)
    }
  }
  return false
}

export async function evaluateAndRunBackup(
  pcr: IPcr,
  serviceId: string,
  authJwt: string,
  cachedMetadata: IPcrBackupMetadata,
  skipIntervalGate = false,
  config?: IPcrBackupConfig
): Promise<BackupResult> {
  if (config?.disabled) {
    return { success: false }
  }

  const pcrId = pcr.pcrId
  const backupKey = `${serviceId}:${pcrId}`

  if (backupInFlight.has(backupKey)) {
    Logger.info(`PcrBackupService: Backup already in progress for pcrId=${pcrId} — skipping`)
    return { success: false }
  }

  // Don't backup while the chart is still loading — PRID may not be persisted yet,
  // which would cause a redundant fallback fetch concurrent with ensurePridFetched.
  if (store.getState().home?.isPCROpening) {
    Logger.info(`PcrBackupService: Chart is opening — deferring backup for pcrId=${pcrId}`)
    return { success: false }
  }

  // Skip backup while submit is in progress — the submit flow already sends the full
  // PCR to the server. Any post-submit changes will trigger a fresh backup via the debounce.
  if (store.getState().submitChart?.inProgress) {
    Logger.info(`PcrBackupService: Submit in progress — skipping backup for pcrId=${pcrId}`)
    return { success: false }
  }

  backupInFlight.add(backupKey)

  try {
    const resolvedConfig = config ?? getPcrBackupConfigSelector(store.getState())
    return await runBackup(pcr, serviceId, authJwt, cachedMetadata, skipIntervalGate, resolvedConfig)
  } finally {
    backupInFlight.delete(backupKey)
  }
}

async function runBackup(
  pcr: IPcr,
  serviceId: string,
  authJwt: string,
  cachedMetadata: IPcrBackupMetadata,
  skipIntervalGate: boolean,
  config: IPcrBackupConfig
): Promise<BackupResult> {
  const pcrId = pcr.pcrId

  if (!isOnline()) {
    Logger.info(`PcrBackupService: Device is offline — skipping backup for pcrId=${pcrId}`)
    return { success: false }
  }

  const metadataService = new PcrBackupMetadataService(serviceId)
  const api = new PcrBackupApiService(authJwt)

  // If cached metadata belongs to a different PCR (stale Redux from chart switch or multi-tab),
  // read fresh metadata from IndexedDB
  let safeMetadata: IPcrBackupMetadata
  if (cachedMetadata?.pcrId === pcrId) {
    safeMetadata = cachedMetadata
  } else {
    const dbMetadata = await metadataService.getMetadata(pcrId)
    safeMetadata = dbMetadata ?? { pcrId, lastSuccessfulBackupTime: null, lastSuccessfulBackupChecksum: null }
  }

  let prid = pcr.prid

  if (!prid) {
    Logger.info(`PcrBackupService: No PRID on pcrId=${pcrId} — performing fallback fetch`)
    try {
      const submitChartService = new SubmitChartService(authJwt)
      const pridResponse: IFetchPRIDResponse = await submitChartService.fetchPRID()
      if (pridResponse?.status !== 200 || !pridResponse.data?.prid) {
        Logger.warn(`PcrBackupService: Fallback PRID fetch failed for pcrId=${pcrId} — backup aborted`)
        return { success: false }
      }
      prid = pridResponse.data.prid.toString()
      store.dispatch(persistPrid(pcrId, prid))
      Logger.info(`PcrBackupService: Fallback PRID for pcrId=${pcrId} → ${prid}`)
    } catch (err) {
      Logger.errorObj(`PcrBackupService: Fallback PRID error for pcrId=${pcrId}`, err)
      return { success: false }
    }
  }

  const effectivePcr = prid ? ({ ...pcr, prid } as IPcr) : pcr
  const checksumInput = buildChecksumInput(effectivePcr)
  const checksum = await getSha256(checksumInput)

  if (safeMetadata?.lastSuccessfulBackupChecksum && safeMetadata.lastSuccessfulBackupChecksum === checksum) {
    Logger.info(`PcrBackupService: Checksum unchanged — skipping backup for pcrId=${pcrId}`)
    return { success: true }
  }

  // skipIntervalGate=true to avoid duplicating the elapsed-time check.
  if (!skipIntervalGate) {
    const nowMs = Date.now()
    if (safeMetadata?.lastSuccessfulBackupTime) {
      const elapsedMs = nowMs - safeMetadata.lastSuccessfulBackupTime
      const minIntervalMs = config.minIntervalMinutes * 60 * 1000
      if (elapsedMs < minIntervalMs) {
        const remainingMin = Math.ceil((minIntervalMs - elapsedMs) / 60000)
        Logger.info(
          `PcrBackupService: Interval not elapsed (${remainingMin} min remaining) — skipping for pcrId=${pcrId}`
        )
        return { success: false }
      }
    } else {
      Logger.info(`PcrBackupService: First eligible backup — proceeding immediately for pcrId=${pcrId}`)
    }
  }

  const serverPayload = buildServerPayload(effectivePcr)
  Logger.info(`PcrBackupService: Sending backup for pcrId=${pcrId} with PRID=${prid}`)
  const success = await sendBackupWithRetry(api, pcrId, serverPayload, config)

  if (success) {
    const updatedMetadata: IPcrBackupMetadata = {
      pcrId,
      lastSuccessfulBackupTime: Date.now(),
      lastSuccessfulBackupChecksum: checksum,
    }
    await metadataService.upsertMetadata(updatedMetadata)

    // Only update Redux if this PCR is still the currently open chart.
    const currentlyOpenPcrId = store.getState().home?.pcrId
    if (currentlyOpenPcrId === pcrId) {
      store.dispatch(setBackupMetadata(updatedMetadata))
    }

    Logger.info(`PcrBackupService: Backup succeeded for pcrId=${pcrId}.`)
    return { success: true }
  } else {
    Logger.warn(`PcrBackupService: All retry attempts failed for pcrId=${pcrId}. Backup markers remain unchanged.`)
    return { success: false }
  }
}
